/**
 * SupportWidget shell. The launcher is rendered with the page (server HTML included); the chat code and its
 * services are loaded after the window `load` event (idle time), or immediately when the visitor clicks.
 * The launcher markup is identical before and after the switch, so nothing moves.
 */
import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildWhatsAppUrl } from '../../utils/whatsapp';
import { SupportLauncher } from './SupportLauncher';

const SupportWidgetLive = lazy(() => import('./SupportWidgetLive'));

const afterLoadAndIdle = (run: () => void): (() => void) => {
    let cancelled = false;
    let handle: number | undefined;
    const schedule = () => {
        if (cancelled) return;
        const idle = (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
        if (idle) idle(run, { timeout: 4000 });
        else handle = window.setTimeout(run, 1500);
    };
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
    return () => {
        cancelled = true;
        window.removeEventListener('load', schedule);
        if (handle !== undefined) window.clearTimeout(handle);
    };
};

export function SupportWidget() {
    const { t } = useTranslation();
    const [live, setLive] = useState(false);
    const [openOnMount, setOpenOnMount] = useState(false);

    useEffect(() => afterLoadAndIdle(() => setLive(true)), []);

    const handleOpen = useCallback(() => {
        setOpenOnMount(true);
        setLive(true);
    }, []);

    const launcher = (
        <SupportLauncher
            onClick={handleOpen}
            label={t('support.openWidget')}
            whatsappUrl={buildWhatsAppUrl(t('whatsapp.messages.general'))}
            whatsappLabel={t('whatsapp.ariaLabel')}
            whatsappText={t('whatsapp.label')}
        />
    );

    if (!live) return launcher;
    return (
        <Suspense fallback={launcher}>
            <SupportWidgetLive openOnMount={openOnMount} />
        </Suspense>
    );
}

export default SupportWidget;
